import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { EmptyState, MetricCard, Panel } from '../../../ui-kit';
import { fetchCwSpotlight, type CwSpotlight } from '../../../services/carrierWebsiteService';
import { useLive } from '../rocco/live';

const EMPTY: CwSpotlight = { runs: 0, stopped: 0, open: 0, fields: 0, checked: 0, refused: 0, guides: 0 };

const STEPS = [
  ['Pick a customer and a carrier', 'A quote request from the website chat, or a file a person confirmed in Carrier Quoting.'],
  ['The Klaris extension opens the website', 'In a new tab in your own browser, so you can watch or step in at any moment.'],
  ['We sign in and fill each screen', 'You enter any security code yourself. Every value is read back from the page and checked against where it came from.'],
  ['We ask when a fact is missing', 'We never guess. The field is highlighted, you fill it in on the website, and we carry on from there.'],
  ['We stop before anything is bought', 'The price is saved here, and a licensed person takes it from there.'],
];

/** The page the Carrier Website Agent opens on. Every figure comes from runs that have happened. */
export default function SpotlightPage() {
  const { data, error, loading } = useLive(fetchCwSpotlight, 10_000);
  const figures = data ?? EMPTY;
  if (error && !data) return <EmptyState message={error} />;
  const share = figures.fields ? `${Math.round((figures.checked / figures.fields) * 100)}%` : '—';
  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' } }}>
        <MetricCard loading={loading} label="Websites filled" value={figures.stopped.toLocaleString()}
          info="Runs that reached the stop point with a price saved." />
        <MetricCard loading={loading} label="Fields filled" value={figures.fields.toLocaleString()}
          info="Values the agent put on a carrier's website." />
        <MetricCard loading={loading} label="Accuracy" value={share}
          info="Share of fields where what the page shows matched what we meant to enter." />
      </Box>
      <Panel title="How a run goes" subtitle="The same for every carrier">
        <Stack spacing={1.5}>
          {STEPS.map(([title, detail], index) => (
            <Stack key={title} direction="row" spacing={1.5} alignItems="flex-start">
              <Typography variant="subtitle2" sx={(t) => ({ color: t.tokens.brand.primary, minWidth: 16 })}>{index + 1}</Typography>
              <Stack>
                <Typography variant="subtitle2" sx={(t) => ({ color: t.tokens.text.primary })}>{title}</Typography>
                <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary })}>{detail}</Typography>
              </Stack>
            </Stack>
          ))}
        </Stack>
      </Panel>
    </Stack>
  );
}
